import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatError } from "@/services/api";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.user?.requires_password_change) {
        window.location.href = "/change-password";
        return;
      }
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm space-y-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <img src="/assets/aptransco-logo.png" alt="APTransco" className="h-12 w-12 object-contain" />
              <div>
                <h1 className="font-heading text-3xl font-black tracking-tight" data-testid="login-title">
                  <span className="text-[#DC2626]">AP</span><span className="text-[#003DA5]">TRANSCO</span>
                </h1>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#71717A]">
                  ServiceDesk
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#52525B]">
              Sign in to the support portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@aptransco.gov.in"
                className="rounded-md border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="rounded-md border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-form-submit-button"
              className="w-full rounded-md bg-[#003DA5] hover:bg-[#002D7A] text-white font-semibold h-11 text-sm"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <p className="text-center text-xs text-[#52525B]">
            Contact your system administrator for account access.
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1591075246923-76a081d21e25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxoaWdoJTIwdm9sdGFnZSUyMHRyYW5zbWlzc2lvbiUyMHRvd2VyJTIwbGF0dGljZSUyMHN0ZWVsJTIwcHlsb258ZW58MHx8fHwxNzc2MDUxODMyfDA&ixlib=rb-4.1.0&q=85"
          alt="High voltage transmission tower"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#003DA5]/60 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-white/90 backdrop-blur-sm p-6 rounded-sm border border-white/20">
            <p className="font-heading text-lg font-bold text-[#09090B]">Power Transmission Support Portal</p>
            <p className="text-sm text-[#52525B] mt-1">Transmission Corporation of Andhra Pradesh — streamlined issue tracking, SLA management, and operational reporting.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
